import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { OperatorsService } from '../../../pages/operators/operators.service';
import { ProfileService } from '../../../common/services/profile.service';
import { User } from '../../../pages/users/models/user.model';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { OperatorDetail } from '../../../pages/operators/models/operator.model';
import { NebularModule } from '../../../common/modules/nebular/nebular.module';
import { TokenService } from '../../../common/services/token.service';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';

interface AvailabilityMessageSettings {
  prefix: string;
  start: string;
  end: string;
  suffix: string;
}

interface AvailabilityDialogResult {
  updateMessage: boolean;
  message?: string;
  settings?: AvailabilityMessageSettings;
}

const DEFAULT_MESSAGE_SETTINGS: AvailabilityMessageSettings = {
  prefix: 'ساعت کاری من از',
  start: '07:00',
  end: '13:00',
  suffix: 'هست.',
};

@Component({
  standalone: true,
  selector: 'ngx-available-toggle',
  templateUrl: './available-toggle.component.html',
  styleUrls: ['./available-toggle.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, NebularModule],
  providers: [OperatorsService],
})
export class AvailableToggleComponent implements OnInit, OnDestroy {
  @ViewChild('availabilityDialog')
  availabilityDialog!: TemplateRef<unknown>;

  isAvailable: boolean = true;
  isUpdating: boolean = false;
  isDialogOpen: boolean = false;
  pendingAvailabilityValue: boolean | null = null;
  readonly timeOptions = Array.from({ length: 48 }, (_, index) => {
    const hour = Math.floor(index / 2)
      .toString()
      .padStart(2, '0');
    const minute = index % 2 === 0 ? '00' : '30';
    return `${hour}:${minute}`;
  });
  readonly rangePresets = [
    { label: '۷ تا ۱۳', start: '07:00', end: '13:00' },
    { label: '۸ تا ۱۶', start: '08:00', end: '16:00' },
    { label: '۹ تا ۱۷', start: '09:00', end: '17:00' },
    { label: '۱۰ تا ۱۸', start: '10:00', end: '18:00' },
  ];
  messageForm: FormGroup;

  private operator: OperatorDetail | null = null;
  private user: User | null = null;
  private userSubscription?: Subscription;

  constructor(
    private operatorsService: OperatorsService,
    private profileService: ProfileService,
    private tokenService: TokenService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private fb: FormBuilder,
  ) {
    this.messageForm = this.fb.group({
      prefix: [DEFAULT_MESSAGE_SETTINGS.prefix, [Validators.maxLength(160)]],
      start: [DEFAULT_MESSAGE_SETTINGS.start, [Validators.required]],
      end: [DEFAULT_MESSAGE_SETTINGS.end, [Validators.required]],
      suffix: [DEFAULT_MESSAGE_SETTINGS.suffix, [Validators.maxLength(160)]],
    });
  }

  ngOnInit(): void {
    const storedUser = this.tokenService.getUser<User>();
    if (storedUser) {
      this.setUser(storedUser);
    }

    this.userSubscription = this.profileService.getUser().subscribe((value) => {
      if (value) {
        this.setUser(value);
      }
    });
  }

  get isBusy(): boolean {
    return this.isUpdating || this.isDialogOpen;
  }

  get hasInvalidTimeRange(): boolean {
    const start = this.messageForm.get('start')?.value;
    const end = this.messageForm.get('end')?.value;

    if (!start || !end) {
      return false;
    }

    return this.timeToMinutes(end) <= this.timeToMinutes(start);
  }

  get messagePreview(): string {
    return this.composeMessage(this.getMessageSettings());
  }

  onStatusChange(value: boolean): void {
    // Keep the toggle on its persisted value while the user decides in the dialog.
    this.isAvailable = !value;

    if (this.isBusy) {
      return;
    }

    const userId = this.getUserId();
    if (!userId) {
      this.toastrService.danger(
        'شناسه کاربری پیدا نشد. لطفاً صفحه را دوباره بارگذاری کنید.',
        'تغییر وضعیت انجام نشد',
      );
      return;
    }

    this.prepareMessageForm();
    this.pendingAvailabilityValue = value;
    this.isDialogOpen = true;

    this.dialogService
      .open(this.availabilityDialog, {
        closeOnBackdropClick: false,
        closeOnEsc: true,
        hasScroll: true,
      })
      .onClose.pipe(take(1))
      .subscribe((result?: AvailabilityDialogResult) => {
        this.isDialogOpen = false;
        this.pendingAvailabilityValue = null;

        if (result) {
          this.updateStatus(value, result);
        }
      });
  }

  usePreset(start: string, end: string): void {
    this.messageForm.patchValue({ start, end });
  }

  submitAvailabilityMessage(dialogRef: NbDialogRef<unknown>): void {
    this.messageForm.markAllAsTouched();

    if (this.messageForm.invalid || this.hasInvalidTimeRange) {
      return;
    }

    const settings = this.getMessageSettings();
    dialogRef.close({
      updateMessage: true,
      message: this.composeMessage(settings),
      settings,
    } as AvailabilityDialogResult);
  }

  changeStatusWithoutMessage(dialogRef: NbDialogRef<unknown>): void {
    dialogRef.close({
      updateMessage: true,
      message: '',
    } as AvailabilityDialogResult);
  }

  private updateStatus(
    value: boolean,
    dialogResult: AvailabilityDialogResult,
  ): void {
    const userId = this.getUserId();
    if (!userId) {
      return;
    }

    const previousValue = this.isAvailable;
    this.isAvailable = value;
    this.isUpdating = true;

    this.operatorsService.updateAvailability(userId, value).subscribe({
      next: () => {
        this.operator = {
          ...(this.operator || ({} as OperatorDetail)),
          isAvailable: value,
        };

        if (dialogResult.updateMessage && dialogResult.message !== undefined) {
          this.updateProfileMessage(
            dialogResult.message,
            dialogResult.settings,
          );
          return;
        }

        this.isUpdating = false;
        this.toastrService.success(
          'وضعیت شما بدون تغییر متن بروزرسانی شد.',
          'انجام شد',
        );
      },
      error: () => {
        this.isAvailable = previousValue;
        this.isUpdating = false;
        this.toastrService.danger(
          'ارتباط با سرور برقرار نشد؛ وضعیت قبلی حفظ شد.',
          'خطا در بروزرسانی',
        );
      },
    });
  }

  private updateProfileMessage(
    message: string,
    settings?: AvailabilityMessageSettings,
  ): void {
    this.profileService.updateProfile({ about: message }).subscribe({
      next: (res) => {
        this.user = res.data || { ...(this.user as User), about: message };
        if (settings) {
          this.saveMessageSettings(settings);
        }
        this.isUpdating = false;
        this.toastrService.success(
          message
            ? 'وضعیت و متن ساعت کاری شما بروزرسانی شد.'
            : 'وضعیت بروزرسانی و متن پروفایل خالی شد.',
          'انجام شد',
        );
      },
      error: () => {
        this.isUpdating = false;
        this.toastrService.warning(
          'وضعیت تغییر کرد، اما متن ساعت کاری ذخیره نشد. دوباره تلاش کنید.',
          'ذخیره ناقص',
        );
      },
    });
  }

  private setUser(user: User): void {
    this.user = user;
    const userId = this.getUserId();

    if (!userId) {
      return;
    }

    this.operatorsService.getById(userId).subscribe((res) => {
      if (this.isUpdating) {
        return;
      }

      this.operator = res.data;
      this.isAvailable = this.operator?.isAvailable ?? false;
    });
  }

  private getUserId(): string | null {
    return this.user?.id || this.user?._id || null;
  }

  private prepareMessageForm(): void {
    const settings =
      this.loadMessageSettings() ||
      this.parseMessage(this.user?.about) ||
      DEFAULT_MESSAGE_SETTINGS;

    this.messageForm.reset(settings);
  }

  private getMessageSettings(): AvailabilityMessageSettings {
    return {
      prefix: String(this.messageForm.get('prefix')?.value || '').trim(),
      start: String(
        this.messageForm.get('start')?.value || DEFAULT_MESSAGE_SETTINGS.start,
      ),
      end: String(
        this.messageForm.get('end')?.value || DEFAULT_MESSAGE_SETTINGS.end,
      ),
      suffix: String(this.messageForm.get('suffix')?.value || '').trim(),
    };
  }

  private composeMessage(settings: AvailabilityMessageSettings): string {
    return [
      settings.prefix,
      settings.start,
      'تا',
      settings.end,
      settings.suffix,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private parseMessage(message?: string): AvailabilityMessageSettings | null {
    const match = message
      ?.trim()
      .match(/^(.*?)\s*(\d{1,2}:\d{2})\s*تا\s*(\d{1,2}:\d{2})\s*(.*?)$/);

    if (!match) {
      return null;
    }

    const start = this.normalizeTime(match[2]);
    const end = this.normalizeTime(match[3]);
    if (!this.timeOptions.includes(start) || !this.timeOptions.includes(end)) {
      return null;
    }

    return {
      prefix: match[1].trim(),
      start,
      end,
      suffix: match[4].trim(),
    };
  }

  private normalizeTime(value: string): string {
    const [hour, minute] = value.split(':');
    return `${hour.padStart(2, '0')}:${minute}`;
  }

  private timeToMinutes(value: string): number {
    const [hour, minute] = value.split(':').map(Number);
    return hour * 60 + minute;
  }

  private get storageKey(): string {
    return `availability-message-settings:${this.getUserId() || 'operator'}`;
  }

  private loadMessageSettings(): AvailabilityMessageSettings | null {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (!saved) {
        return null;
      }

      const parsed = JSON.parse(saved) as AvailabilityMessageSettings;
      if (
        typeof parsed.prefix !== 'string' ||
        typeof parsed.suffix !== 'string' ||
        !this.timeOptions.includes(parsed.start) ||
        !this.timeOptions.includes(parsed.end)
      ) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  private saveMessageSettings(settings: AvailabilityMessageSettings): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
    } catch {
      // Private browsing can make storage unavailable; the server update still succeeds.
    }
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
  }
}
